import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  IconButton,
  Paper,
  Slide,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import GetAppIcon from '@mui/icons-material/GetApp';
import IosShareIcon from '@mui/icons-material/IosShare';
import AddBoxOutlinedIcon from '@mui/icons-material/AddBoxOutlined';

const DISMISS_KEY = 'pwa-install-dismissed-at';
const DISMISS_DAYS = 7;

// beforeinstallprompt se dispara al cargar la página, generalmente antes de que
// este componente se monte (login mediante), así que se captura a nivel módulo.
let deferredPrompt = null;
const listeners = new Set();

if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (event) => {
    event.preventDefault();
    deferredPrompt = event;
    listeners.forEach((listener) => listener(event));
  });
  window.addEventListener('appinstalled', () => {
    deferredPrompt = null;
    listeners.forEach((listener) => listener(null));
  });
}

const isStandalone = () =>
  window.matchMedia('(display-mode: standalone)').matches ||
  window.navigator.standalone === true;

const isIos = () =>
  /iphone|ipad|ipod/i.test(window.navigator.userAgent) ||
  // iPad con iPadOS se identifica como Mac pero tiene pantalla táctil
  (window.navigator.platform === 'MacIntel' && window.navigator.maxTouchPoints > 1);

const wasRecentlyDismissed = () => {
  const dismissedAt = Number(localStorage.getItem(DISMISS_KEY));
  if (!dismissedAt) return false;
  return Date.now() - dismissedAt < DISMISS_DAYS * 24 * 60 * 60 * 1000;
};

const InstallPrompt = () => {
  const [installEvent, setInstallEvent] = useState(deferredPrompt);
  const [dismissed, setDismissed] = useState(wasRecentlyDismissed);

  useEffect(() => {
    const listener = (event) => setInstallEvent(event);
    listeners.add(listener);
    return () => listeners.delete(listener);
  }, []);

  if (isStandalone() || dismissed) return null;

  const ios = isIos();
  if (!ios && !installEvent) return null;

  const handleDismiss = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setDismissed(true);
  };

  const handleInstall = async () => {
    if (!installEvent) return;
    installEvent.prompt();
    const { outcome } = await installEvent.userChoice;
    deferredPrompt = null;
    setInstallEvent(null);
    if (outcome === 'dismissed') handleDismiss();
  };

  return (
    <Slide direction="up" in mountOnEnter>
      <Paper
        elevation={6}
        sx={{
          position: 'fixed',
          bottom: 16,
          left: 16,
          right: 16,
          maxWidth: 480,
          mx: 'auto',
          p: 2,
          zIndex: (theme) => theme.zIndex.snackbar,
          borderRadius: '0.75rem',
        }}
      >
        <Box display="flex" alignItems="flex-start">
          <Box
            component="img"
            src="/icon-192.png"
            alt="Capriata"
            sx={{ width: 48, height: 48, borderRadius: '0.5rem', mr: 1.5 }}
          />
          <Box flexGrow={1}>
            <Typography variant="subtitle1" fontWeight={600}>
              Instalá la app de Capriata
            </Typography>
            {ios ? (
              <Typography variant="body2" color="text.secondary">
                Tocá <IosShareIcon sx={{ fontSize: 18, verticalAlign: 'text-bottom' }} />{' '}
                <strong>Compartir</strong> y luego{' '}
                <AddBoxOutlinedIcon sx={{ fontSize: 18, verticalAlign: 'text-bottom' }} />{' '}
                <strong>Agregar a inicio</strong> para tenerla en tu pantalla.
              </Typography>
            ) : (
              <Typography variant="body2" color="text.secondary">
                Accedé más rápido desde tu pantalla de inicio.
              </Typography>
            )}
            {!ios && (
              <Button
                variant="contained"
                size="small"
                startIcon={<GetAppIcon />}
                onClick={handleInstall}
                sx={{ mt: 1 }}
              >
                Instalar
              </Button>
            )}
          </Box>
          <IconButton size="small" onClick={handleDismiss} aria-label="Cerrar">
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </Paper>
    </Slide>
  );
};

export default InstallPrompt;
